// ActiveAdmin JavaScript entry point
// No jQuery required - ActiveAdmin 2+ works without it

// Rails UJS for data-method, data-confirm, etc. (used in ActiveAdmin)
import Rails from '@rails/ujs'
Rails.start()

// Import our custom has_many initializer (replaces ActiveAdmin's jQuery version)
// This file handles the "Add" and "Remove" buttons for has_many forms
import './active_admin/has-many'

// Import other dependencies
import toastr from 'toastr'
import intlTelInput from 'intl-tel-input/build/js/intlTelInput'
import _ from 'lodash'
import SlimSelect from 'slim-select'
import flatpickr from 'flatpickr'
// Flatpickr CSS is imported in SCSS, no need to import here
// import 'flatpickr/dist/flatpickr.min.css'

// Make them globally available
window.toastr = toastr
// intl-tel-input exports the function directly (not as default)
window.intlTelInput = intlTelInput
window._ = _
window.SlimSelect = SlimSelect
window.flatpickr = flatpickr

// Import local modules
import './active_admin/map'
import './active_admin/pretty_input_file'
import './active_admin/affect_installer'
import './active_admin/companies_prospected_area'
import './active_admin/company_add_user'
import './active_admin/company_toggle_notified_by_sms'
import './active_admin/user_creator'
import './active_admin/user_toggle_notified_by_sms'
import './active_admin/combobox' // Vanilla JS combobox for simple selects

function setupIntlTelInput() {
  document.querySelectorAll('input[type=tel]').forEach((element) => {
    if (element._iti) return

    try {
      const options = {
        initialCountry: 'fr',
        separateDialCode: true,
      }

      if (window.intlTelInput) {
        const iti = window.intlTelInput(element, options)
        // Store the instance on the element for later access
        element._iti = iti
      } else {
        console.error('intlTelInput is not available')
      }
    } catch (error) {
      console.error('Error setting up intlTelInput:', error, element)
    }
  })
}

// Prepend dial code on form submit
document.addEventListener('submit', (event) => {
  const form = event.target
  if (!form || form.tagName !== 'FORM') return

  const telInputs = form.querySelectorAll('input[type=tel]')

  telInputs.forEach((input) => {
    if (input._iti) {
      const countryData = input._iti.getSelectedCountryData()
      const dialCode = countryData.dialCode
      const currentValue = input.value.trim()

      if (currentValue && !currentValue.startsWith('+')) {
        const cleanedValue = currentValue.replace(/^0+/, '')
        input.value = '+' + dialCode + cleanedValue
      }
    }
  })
}, true)

function onFirstChildChange(containerSelector, callback) {
  const container = document.querySelector(containerSelector)
  if (!container) return

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        callback.call()
        observer.disconnect()
      }
    }
  })

  observer.observe(container, { childList: true })
}

document.addEventListener("click", (event) => {
  const btn = event.target.closest(".clear_filters_btn")
  if (!btn) return

  event.preventDefault()
  window.location = window.location.pathname
})

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Configure toastr
  if (window.toastr) {
    window.toastr.options.closeButton = true
    window.toastr.options.closeDuration = 3000
    window.toastr.options.progressBar = true
  }

  // Copy to clipboard functionality
  document.querySelectorAll('.copy-to-clipboard').forEach((button) => {
    button.addEventListener('click', () => {
      const linkContainer = button.parentElement.querySelector('textarea.link-container')
      if (linkContainer) {
        linkContainer.focus()
        linkContainer.select()
        try {
          document.execCommand('copy')
          if (window.toastr) {
            window.toastr.success('Lien copié vers le presse-papier')
          }
        } catch (err) {
          if (window.toastr) {
            window.toastr.warning("Le lien n'a pas pu être copié vers le presse-papier")
          }
        }
      }
    })
  })

  setupIntlTelInput()

  // Initialize search_select inputs with SlimSelect
  function initializeSearchSelects(container = document) {
    if (!window.SlimSelect) return

    const inputs = container.querySelectorAll('select.search-select-input')
    if (inputs.length === 0) return

    inputs.forEach((select) => {
      // Skip if already initialized
      if (select._slimSelect) return

      const url = select.dataset.searchSelectUrl
      const fields = JSON.parse(select.dataset.searchSelectFields || '[]')
      const predicate = select.dataset.searchSelectPredicate || 'contains'
      const displayName = select.dataset.searchSelectDisplayName || 'name'
      const minimumInputLength = parseInt(select.dataset.searchSelectMinimumInputLength || '2', 10)
      const orderBy = select.dataset.searchSelectOrderBy || 'id_desc'

      const slimSelect = new window.SlimSelect({
        select: select,
        settings: {
          placeholder: 'Rechercher...',
          allowDeselect: true,
          searchPlaceholder: 'Tapez pour rechercher...',
          searchText: 'Aucun résultat',
          searchingText: 'Recherche...',
        },
        events: {
          search: (search, currentData) => {
            return new Promise((resolve, reject) => {
              if (search.length < minimumInputLength) {
                reject(`Veuillez entrer au moins ${minimumInputLength} caractères`)
                return
              }

              // Build query string
              // ActiveAdmin search_select uses a specific format with groupings
              const params = new URLSearchParams()
              if (fields.length > 0) {
                // Format: q[groupings][0][search_term_contains]=search
                fields.forEach((field, index) => {
                  const paramName = `q[groupings][${index}][${field}_${predicate}]`
                  params.append(paramName, search)
                })
              } else {
                // Fallback if no fields specified
                params.append('q[groupings][0][search_term_contains]', search)
              }
              if (orderBy) {
                params.append('order_by', orderBy)
              }

              const csrfTokenEl = document.querySelector('meta[name="csrf-token"]')
              const csrfToken = csrfTokenEl ? csrfTokenEl.getAttribute('content') : null

              fetch(`${url}?${params.toString()}`, {
                method: 'GET',
                headers: {
                  'X-CSRF-TOKEN': csrfToken,
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                },
              })
                .then((response) => response.json())
                .then((data) => {
                  // Handle different response formats
                  let items = []
                  if (Array.isArray(data)) {
                    items = data
                  } else if (data.data && Array.isArray(data.data)) {
                    items = data.data.map((item) => item.attributes || item)
                  } else if (data.results && Array.isArray(data.results)) {
                    items = data.results
                  }

                  const options = items.map((item) => {
                    // Handle simple format: { id: "123", full_name: "John Doe" }
                    const value = item.id || item.value || (item.attributes && item.attributes.id)
                    let text = item[displayName]
                    if (!text && item.attributes) {
                      text = item.attributes[displayName] || item.attributes.name || item.attributes.full_name
                    }
                    if (!text) {
                      text = item.name || item.text || item.full_name || value
                    }
                    return { value: String(value), text: String(text) }
                  })
                  resolve(options)
                })
                .catch((error) => {
                  console.error('Error fetching search results:', error)
                  reject('Erreur lors de la recherche')
                })
            })
          },
          afterChange: (newVal) => {
            // Trigger change event on the native select
            select.dispatchEvent(new Event('change', { bubbles: true }))
          },
        },
      })

      select._slimSelect = slimSelect
    })
  }

  // Make it globally available for re-initialization after dynamic content changes
  window.initializeSearchSelects = initializeSearchSelects

  // Initialize search selects
  setTimeout(() => initializeSearchSelects(), 200)
  setTimeout(() => initializeSearchSelects(), 1000)

  // Initialize multiple selects with SlimSelect (better UX than native multiple)
  function initializeMultipleSelects(container = document) {
    if (!window.SlimSelect) return

    const selects = container.querySelectorAll('select[multiple]:not(.search-select-input):not([data-combobox-disabled])')
    if (selects.length === 0) return

    selects.forEach((select) => {
      // Skip if already initialized
      if (select._slimSelect) return

      const slimSelect = new window.SlimSelect({
        select: select,
        settings: {
          placeholder: 'Sélectionner...',
          allowDeselect: true,
          closeOnSelect: false, // Keep open for multiple selections
          searchPlaceholder: 'Rechercher...',
          searchText: 'Aucun résultat',
          searchingText: 'Recherche...',
        },
        events: {
          afterChange: (newVal) => {
            // SlimSelect should already sync to native select automatically
            // Just trigger change event to notify other listeners
            try {
              // Use setTimeout to ensure SlimSelect has finished updating the native select
              setTimeout(() => {
                const changeEvent = new Event('change', { bubbles: true })
                select.dispatchEvent(changeEvent)
              }, 0)
            } catch (error) {
              console.error('Error triggering change event:', error)
            }
          },
        },
      })

      select._slimSelect = slimSelect
    })
  }

  // Make it globally available
  window.initializeMultipleSelects = initializeMultipleSelects

  // Initialize multiple selects
  setTimeout(() => initializeMultipleSelects(), 200)
  setTimeout(() => initializeMultipleSelects(), 1000)

  // Initialize vanilla comboboxes for simple selects
  if (window.initializeComboboxes) {
    setTimeout(() => window.initializeComboboxes(), 200)
    setTimeout(() => window.initializeComboboxes(), 1000)
  }

  // Initialize datepickers with Flatpickr (vanilla JS, no jQuery needed)
  function initializeDatePickers(container = document) {
    if (!window.flatpickr) return

    // Find all datepicker inputs - ActiveAdmin uses class 'datepicker' for datepicker inputs
    // Also check for inputs with data-datepicker-options attribute
    const inputs = container.querySelectorAll('input.datepicker, input.date-time-picker-input, input[type="text"][id*="date"], input[type="text"][id*="Date"], input[data-datepicker-options], input[data-picker-options]')
    if (inputs.length === 0) return

    inputs.forEach((input) => {
      // Skip if already initialized
      if (input._flatpickr) return

      // Skip disabled inputs
      if (input.disabled) return

      // Get options from data attribute
      // ActiveAdmin stores options as JSON in data-datepicker-options
      let options = {
        dateFormat: 'Y-m-d',
        allowInput: true,
        // IMPORTANT: inline must be false to show calendar only on click
        inline: false,
        // Show calendar on focus/click
        clickOpens: true,
        // Static positioning for better layout
        static: false,
      }

      const attrValue = input.getAttribute('data-datepicker-options') || input.getAttribute('data-picker-options')
      if (attrValue) {
        try {
          // Decode HTML entities and parse JSON
          const decoded = attrValue.replace(/&quot;/g, '"').replace(/&#39;/g, "'")
          const parsed = JSON.parse(decoded)

          // Convert ActiveAdmin datepicker options to Flatpickr format
          if (parsed.minDate || parsed.min_date) {
            options.minDate = parsed.minDate || parsed.min_date
          }
          if (parsed.maxDate || parsed.max_date) {
            options.maxDate = parsed.maxDate || parsed.max_date
          }
          // For datetime inputs, enable time picker
          if (input.classList.contains('date-time-picker-input')) {
            options.enableTime = true
            options.time_24hr = true
            options.dateFormat = 'Y-m-d H:i'
          }
          // NEVER allow inline mode - always show calendar on click only
          // This overrides any inline setting from parsed options
          options.inline = false
          // For datetime inputs, enable time picker
          if (input.classList.contains('date-time-picker-input')) {
            options.enableTime = true
            options.time_24hr = true
            options.dateFormat = 'Y-m-d H:i'
          }
        } catch (e) {
          console.warn('Failed to parse datepicker options:', e, attrValue)
        }
      }

      window.flatpickr(input, options)
    })
  }

  // Make it globally available
  window.initializeDatePickers = initializeDatePickers

  // Initialize on load
  setTimeout(() => initializeDatePickers(), 200)
  setTimeout(() => initializeDatePickers(), 1000)
})

// Export helpers for use in other modules
window.setupIntlTelInput = setupIntlTelInput
window.onFirstChildChange = onFirstChildChange
